"use client";

import {
  AB_COOKIE_MAX_AGE,
  AB_VARIANT_COOKIE,
  AB_VISITOR_COOKIE,
  isAbVariant,
  type AbEvent,
  type AbVariant,
} from "@/lib/ab-test-config";

function readCookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

function writeCookie(name: string, value: string) {
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${value}; Path=/; Max-Age=${AB_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

export function hasAbAssignment(): boolean {
  return isAbVariant(readCookie(AB_VARIANT_COOKIE));
}

export type AbAssignment = { variant: AbVariant; preview: boolean };

// The visitor's variant, assigned 50/50 on first sight and then sticky.
// `?ab=a` / `?ab=b` previews a variant without touching the cookies; preview
// views must not be tracked. Memoised: useSyncExternalStore needs a stable
// snapshot, and the assignment can't change during a page's life.
let assignment: AbAssignment | undefined;
export function getAbAssignment(): AbAssignment {
  return (assignment ??= assign());
}

// Just the `?ab=` preview, for drafting variant B while the test is off.
let preview: AbAssignment | null | undefined;
export function getAbPreview(): AbAssignment | null {
  if (preview === undefined) {
    const forced = new URLSearchParams(location.search).get("ab");
    preview = isAbVariant(forced) ? { variant: forced, preview: true } : null;
  }
  return preview;
}

function assign(): AbAssignment {
  const forced = getAbPreview();
  if (forced) return forced;

  let variant = readCookie(AB_VARIANT_COOKIE);
  if (!isAbVariant(variant)) {
    variant = Math.random() < 0.5 ? "a" : "b";
    writeCookie(AB_VARIANT_COOKIE, variant);
  }
  if (!readCookie(AB_VISITOR_COOKIE)) writeCookie(AB_VISITOR_COOKIE, crypto.randomUUID());
  return { variant: variant as AbVariant, preview: false };
}

// Fire-and-forget; the server ignores it outside production, for bots and
// admins, and while the test is off. Dev never sends (shared database).
export function sendAbEvent(event: AbEvent) {
  if (process.env.NODE_ENV !== "production") return;
  fetch("/api/ab", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ event }),
    keepalive: true,
  }).catch(() => {});
}
